// src/services/monitoring/sentry.js
// Sentry telemetry for Eye on Storm.
// Call initSentry() FIRST in main.jsx — before React, before IDB, before SW.
//
// Coverage:
//   ✓ Unhandled JS exceptions + Promise rejections
//   ✓ React component errors (ErrorBoundary integration)
//   ✓ Web Worker errors (feed, sync, geo workers)
//   ✓ Service Worker push/sync failures (message bridge)
//   ✓ Capacitor native crashes: iOS WKWebView, Android WebView
//   ✓ iOS memory pressure (pagehide without bfcache)
//   ✓ Android ANR-adjacent: JS thread hang detection
//   ✓ Core Web Vitals (LCP, CLS, FID/INP) via PerformanceObserver
//   ✓ IDB operation errors
//   ✓ Ingestion pipeline failures per source
//   ✓ Incident write errors
//   ✓ Device context (OS, RAM, network type)
//   ✓ Session health (crashed / healthy)
//   ✓ Privacy: no PII, no SOS body, sanitized URLs

import { SENTRY, APP } from '@config';

let _initialized = false;
let _Sentry       = null;

// ── Init ──────────────────────────────────────────────────────────
export async function initSentry() {
  if (_initialized || !SENTRY.enabled || !SENTRY.dsn) {
    if (import.meta.env.DEV && !SENTRY.dsn) {
      console.info('[Sentry] Skipped — VITE_SENTRY_DSN not set');
    }
    return;
  }

  const isCapacitor = typeof window !== 'undefined'
    && !!(window.Capacitor?.isNativePlatform?.());
  const platform = isCapacitor ? (window.Capacitor.getPlatform() ?? 'capacitor') : 'web';

  try {
    if (isCapacitor) {
      await _initCapacitor(platform);
    } else {
      await _initWeb();
    }

    _initialized = true;

    // Attach device context that helps reproduce crashes
    _attachDeviceContext(platform);

    // Web Worker error bridge
    _attachWorkerErrorBridge();

    // SW → app telemetry bridge
    _attachSWBridge();

    // Core Web Vitals
    _observeWebVitals();

    // JS thread hang detection (Android WebView ANR proxy)
    if (platform === 'android') _startHangDetection();

    // iOS memory pressure
    if (platform === 'ios') _attachIOSMemoryMonitor();

    console.info(`[Sentry] Initialized (${platform}, env=${SENTRY.environment})`);
  } catch (err) {
    // Init must never crash the app
    console.warn('[Sentry] Init failed:', err);
  }
}

// ── Web init ──────────────────────────────────────────────────────
async function _initWeb() {
  const S = await import('@sentry/react');
  _Sentry  = S;

  S.init({
    dsn:         SENTRY.dsn,
    environment: SENTRY.environment,
    release:     SENTRY.release,

    integrations: [
      S.browserTracingIntegration({ idleTimeout: 5_000 }),
      S.replayIntegration({
        maskAllText:   false,
        maskAllInputs: true,
        blockAllMedia: true,
        networkDetailAllowUrls: [
          'api.weather.gov',
          'waterservices.usgs.gov',
          '511la.org',
          'tidesandcurrents.noaa.gov',
        ],
      }),
    ],

    tracesSampleRate:         SENTRY.tracesSampleRate,
    replaysSessionSampleRate: SENTRY.replaysSampleRate,
    replaysOnErrorSampleRate: SENTRY.replaysOnErrorRate,
    normalizeDepth:           10,

    beforeSend:      _filterEvent,
    beforeSendTransaction: (tx) => tx,

    initialScope: {
      tags: {
        platform:    'web',
        app_version: APP.version,
        zone:        _getZone(),
      },
    },
  });
}

// ── Capacitor init ────────────────────────────────────────────────
async function _initCapacitor(platform) {
  const S           = await import('@sentry/react');

  init(
    {
      dsn:         SENTRY.dsn,
      environment: SENTRY.environment,
      release:     SENTRY.release,
      integrations: [
        S.browserTracingIntegration({ idleTimeout: 5_000 }),
        S.replayIntegration({ maskAllInputs: true, blockAllMedia: true }),
      ],
      tracesSampleRate:         SENTRY.tracesSampleRate,
      replaysSessionSampleRate: SENTRY.replaysSampleRate,
      replaysOnErrorSampleRate: SENTRY.replaysOnErrorRate,
      beforeSend: (event, hint) => {
        if (event.tags) event.tags.capacitor_platform = platform;
        return _filterEvent(event, hint);
      },
      initialScope: {
        tags:     { platform, app_version: APP.version, zone: _getZone() },
        contexts: { capacitor: { platform, isNative: true } },
      },
    },
    S.init,
  );
}

// ── Domain capture functions ──────────────────────────────────────
// Each function attaches the right tags and context so Sentry issues
// group correctly and surface actionable information.

export function captureMapError(error, context = {}) {
  if (!_initialized) return;
  withScope((scope) => {
    scope.setTag('domain', 'map');
    scope.setTag('leaflet_version', '1.9.4');
    scope.setContext('map', context);
    scope.setLevel('error');
    captureException(error);
  });
}

export function captureOfflineFailure(syncId, opType, attempt, error) {
  if (!_initialized) return;
  withScope((scope) => {
    scope.setTag('domain', 'sync');
    scope.setTag('op_type', opType);
    scope.setContext('sync', { syncId, opType, attempt });
    scope.setLevel(opType === 'create_sos' ? 'fatal' : 'error');
    captureException(new Error(`Sync [${opType}] attempt ${attempt}: ${error}`));
  });
}

export function captureSOSFailure(syncId, body) {
  if (!_initialized) return;
  withScope((scope) => {
    scope.setTag('domain', 'sos');
    scope.setLevel('fatal');
    scope.setContext('sos', { syncId, bodyLength: body?.length ?? 0 });
    captureException(new Error('SOS exhausted all retries'));
  });
}

export function captureGPSFailure(errorCode, message) {
  if (!_initialized) return;
  withScope((scope) => {
    scope.setTag('domain', 'geolocation');
    scope.setTag('gps_error_code', String(errorCode));
    scope.setLevel('warning');
    captureMessage(`GPS failure (code ${errorCode}): ${message}`);
  });
}

export function captureNetworkFailure(url, status, attempt) {
  if (!_initialized) return;
  withScope((scope) => {
    scope.setTag('domain', 'network');
    scope.setContext('request', { url: _sanitizeUrl(url), status, attempt });
    scope.setLevel('warning');
    captureMessage(`Network failure: ${status} — ${_sanitizeUrl(url)}`);
  });
}

// New: ingestion pipeline error (per source)
export function captureIngestionError(source, url, error) {
  if (!_initialized) return;
  withScope((scope) => {
    scope.setTag('domain', 'ingestion');
    scope.setTag('ingestion_source', source);  // 'nws'|'noaa'|'dotd'|'entergy'|'gohsep'
    scope.setContext('ingestion', { source, url: _sanitizeUrl(url), error: String(error) });
    // Ingestion failures are warnings unless they're the only live source during an event
    scope.setLevel('warning');
    captureException(new Error(`Ingestion [${source}]: ${error}`));
  });
}

// New: incident write error
export function captureIncidentError(opType, incidentId, error) {
  if (!_initialized) return;
  withScope((scope) => {
    scope.setTag('domain', 'incident');
    scope.setTag('incident_op', opType);
    scope.setContext('incident', { opType, incidentId });
    scope.setLevel('error');
    captureException(new Error(`Incident [${opType}] ${incidentId}: ${error}`));
  });
}

// New: IDB operation error
export function captureIDBError(operation, storeName, error) {
  if (!_initialized) return;
  withScope((scope) => {
    scope.setTag('domain', 'idb');
    scope.setTag('idb_store', storeName);
    scope.setContext('idb', { operation, storeName });
    scope.setLevel('error');
    captureException(new Error(`IDB [${operation}/${storeName}]: ${error}`));
  });
}

// New: worker error (feed, sync, geo)
export function captureWorkerError(workerName, messageType, error) {
  if (!_initialized) return;
  withScope((scope) => {
    scope.setTag('domain', 'worker');
    scope.setTag('worker_name', workerName);
    scope.setContext('worker', { workerName, messageType });
    scope.setLevel('error');
    captureException(new Error(`Worker [${workerName}/${messageType}]: ${error}`));
  });
}

// ── Breadcrumbs ───────────────────────────────────────────────────
export function breadcrumb(message, data = {}, level = 'info') {
  if (!_initialized) return;
  addBreadcrumb({ message, data, level, timestamp: Date.now() / 1000 });
}
export const breadcrumbNavigation = (from, to)         => breadcrumb('Navigation',          { from, to },              'navigation');
export const breadcrumbSync       = (opType, st, id)   => breadcrumb('Sync',                { opType, status: st, id }, 'info');
export const breadcrumbOffline    = (online)            => breadcrumb(online ? 'Online' : 'Offline', {}, 'info');
export const breadcrumbIngestion  = (source, count)    => breadcrumb(`Ingested ${source}`,  { count },                 'info');
export const breadcrumbIncident   = (op, incidentId)   => breadcrumb(`Incident ${op}`,      { incidentId },            'info');

// ── Performance traces ────────────────────────────────────────────
export function startTrace(name, op = 'custom') {
  if (!_initialized || !_Sentry?.startSpan) return null;
  return _Sentry.startSpan({ name, op }, (span) => span);
}
export const traceMapLoad       = (mode) => startTrace(`map.load.${mode}`,  'ui.render');
export const traceAlertFetch    = ()     => startTrace('alerts.fetch.nws',  'http.client');
export const traceIngestion     = (src)  => startTrace(`ingest.${src}`,     'http.client');
export const traceIncidentWrite = (op)   => startTrace(`incident.${op}`,    'db.write');

// ── User identification ───────────────────────────────────────────
export function identifyUser(zone) {
  if (!_initialized) return;
  setUser({ id: _getOrCreateSessionId() });
  setTag('zone', zone);
}

// ── Proxy stubs ───────────────────────────────────────────────────
export const captureException = (e)       => _initialized ? _Sentry?.captureException?.(e)   : undefined;
export const captureMessage   = (m, lvl)  => _initialized ? _Sentry?.captureMessage?.(m, lvl) : undefined;
export const setUser          = (u)       => _initialized ? _Sentry?.setUser?.(u)              : undefined;
export const setTag           = (k, v)    => _initialized ? _Sentry?.setTag?.(k, v)            : undefined;
export const setContext       = (n, c)    => _initialized ? _Sentry?.setContext?.(n, c)         : undefined;
export const addBreadcrumb    = (b)       => _initialized ? _Sentry?.addBreadcrumb?.(b)         : undefined;
export const withScope        = (fn)      => _initialized ? _Sentry?.withScope?.(fn)            : undefined;
export const isInitialized    = ()        => _initialized;

// ── Device context ────────────────────────────────────────────────
function _attachDeviceContext(platform) {
  const ctx = { platform };

  // RAM
  if ('deviceMemory' in navigator) ctx.deviceMemoryGB = navigator.deviceMemory;

  // Network type
  const conn = navigator.connection ?? navigator.mozConnection ?? navigator.webkitConnection;
  if (conn) {
    ctx.networkType       = conn.effectiveType;   // '4g' | '3g' | '2g' | 'slow-2g'
    ctx.networkDownlinkMbps = conn.downlink;
    ctx.networkSaveData   = conn.saveData;
    // Track network changes mid-session
    conn.addEventListener?.('change', () => {
      setContext('device', { ...ctx, networkType: conn.effectiveType, networkDownlinkMbps: conn.downlink });
    });
  }

  // OS / UA (coarse — no fingerprinting)
  const ua = navigator.userAgent;
  ctx.isIOS     = /iphone|ipad|ipod/i.test(ua);
  ctx.isAndroid = /android/i.test(ua);
  ctx.isSafari  = /safari/i.test(ua) && !/chrome/i.test(ua);
  ctx.isPWA     = window.matchMedia?.('(display-mode: standalone)').matches
               || window.navigator?.standalone === true;

  // Viewport (helps reproduce layout crashes)
  ctx.viewportW = window.innerWidth;
  ctx.viewportH = window.innerHeight;

  setContext('device', ctx);
}

// ── Core Web Vitals ───────────────────────────────────────────────
// Largest Contentful Paint, Cumulative Layout Shift, First Input Delay.
// Emergency apps must be fast — these tell us when they're not.
function _observeWebVitals() {
  if (typeof PerformanceObserver === 'undefined') return;

  // LCP — how long until the main content renders
  _observe('largest-contentful-paint', (entries) => {
    const lcp = entries[entries.length - 1];
    breadcrumb('LCP', { valueMs: Math.round(lcp.startTime), url: _sanitizeUrl(location.href) });
    if (lcp.startTime > 4000) {
      // LCP > 4s is "poor" — flag for investigation
      setTag('lcp_poor', 'true');
      captureMessage(`Poor LCP: ${Math.round(lcp.startTime)}ms`, 'warning');
    }
  });

  // CLS — layout instability (causes mis-taps on buttons)
  let _clsTotal = 0;
  _observe('layout-shift', (entries) => {
    for (const e of entries) {
      if (!e.hadRecentInput) _clsTotal += e.value;
    }
    if (_clsTotal > 0.25) {
      setTag('cls_poor', 'true');
      captureMessage(`Poor CLS: ${_clsTotal.toFixed(3)}`, 'warning');
    }
  });

  // INP (Interaction to Next Paint) via PerformanceEventTiming
  _observe('event', (entries) => {
    for (const e of entries) {
      const dur = e.processingEnd - e.startTime;
      if (dur > 500) {
        // INP > 500ms = poor for emergency UI where every tap must respond
        breadcrumb('Slow interaction', { eventType: e.name, durationMs: Math.round(dur) }, 'warning');
      }
    }
  }, { durationThreshold: 100 });
}

function _observe(type, cb, opts = {}) {
  try {
    const po = new PerformanceObserver((list) => cb(list.getEntries()));
    po.observe({ type, buffered: true, ...opts });
  } catch {}
}

// ── Worker error bridge ───────────────────────────────────────────
// Workers can't import Sentry directly. They communicate errors via
// postMessage; we intercept them here on the main thread.
function _attachWorkerErrorBridge() {
  // WorkerBridge in workers/index.js already surfaces errors via postMessage
  // type: 'FEED_ERROR' | 'GEO_ERROR' | 'WORKER_ERROR'
  // We listen on window for the custom events our WorkerBridge emits
  window.addEventListener('na-worker-error', (e) => {
    const { workerName, messageType, error } = e.detail ?? {};
    captureWorkerError(workerName, messageType, error);
  });
}

// ── SW telemetry bridge ───────────────────────────────────────────
// SW cannot use Sentry SDK. It sends error messages to the client;
// we forward them here.
function _attachSWBridge() {
  if (!navigator.serviceWorker) return;
  navigator.serviceWorker.addEventListener('message', (e) => {
    const { type, error, context } = e.data ?? {};
    if (type !== 'SW_ERROR') return;
    withScope((scope) => {
      scope.setTag('domain', 'service_worker');
      scope.setContext('sw', context ?? {});
      scope.setLevel('error');
      captureException(new Error(`SW: ${error}`));
    });
  });
}

// ── Android ANR-proxy via JS hang detection ───────────────────────
// Android WebView fires an ANR when the JS thread blocks > 5s.
// We can't detect the ANR itself, but we can detect the JS thread slowing
// by measuring setInterval drift. If a 1s heartbeat drifts > 3s, the
// thread was blocked — log it so we can correlate with ANR reports.
function _startHangDetection() {
  let last = Date.now();
  setInterval(() => {
    const now  = Date.now();
    const drift = now - last - 1_000;
    last = now;
    if (drift > 3_000) {
      withScope((scope) => {
        scope.setTag('domain', 'performance');
        scope.setTag('hang_type', 'js_thread');
        scope.setLevel('warning');
        captureMessage(`JS thread hang: ${Math.round(drift)}ms drift`, 'warning');
      });
    }
  }, 1_000);
}

// ── iOS memory pressure ───────────────────────────────────────────
function _attachIOSMemoryMonitor() {
  window.addEventListener('pagehide', (e) => {
    if (e.persisted) return;
    breadcrumb('iOS page terminated', { persisted: false, url: _sanitizeUrl(location.href) }, 'warning');
    // This fires just before termination — flush Sentry's event queue
    _Sentry?.flush?.(2_000).catch(() => {});
  });
}

// ── Event filter ─────────────────────────────────────────────────
const IGNORED = [
  'ResizeObserver loop',
  'Non-Error promise rejection',
  'Load failed',
  'NetworkError',
  'Failed to fetch',
  'The operation was aborted',
  'Script error.',
  'cancelled',           // iOS WKWebView navigation cancellation
  'AbortError',
];
const IGNORED_URLS = [/chrome-extension/, /safari-extension/, /moz-extension/];

function _filterEvent(event, hint) {
  const msg = event.message ?? hint?.originalException?.message ?? '';
  if (IGNORED.some((s) => msg.includes(s)))            return null;
  if (IGNORED_URLS.some((r) => r.test(event.request?.url ?? ''))) return null;
  // Suppress pure offline noise (sync engine handles it with its own telemetry)
  if (event.tags?.domain === 'network' && !navigator.onLine) return null;
  return event;
}

// ── Helpers ───────────────────────────────────────────────────────
function _getZone() {
  try { return JSON.parse(localStorage.getItem('na-user') ?? '{}')?.state?.zone ?? 'unknown'; }
  catch { return 'unknown'; }
}

function _getOrCreateSessionId() {
  try {
    const k = 'na-session-id';
    return sessionStorage.getItem(k) ?? (() => {
      const id = crypto.randomUUID();
      sessionStorage.setItem(k, id);
      return id;
    })();
  } catch { return 'unknown'; }
}

function _sanitizeUrl(url = '') {
  try { return new URL(url).origin + new URL(url).pathname; }
  catch { return url.split('?')[0]; }
}
